import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import TimeSlotSelection from '../components/TimeSlotSelection';
import { RootState } from '../redux/store';
import { setSelectedSlot, setSelectedOperatories, setPrimetimeFilter } from '../redux/slices/appointmentSlice';
import { AvailableSlotsResponse, fetchAvailableSlots } from '../services/api';
import { SyncLoader } from 'react-spinners';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import Toggle from '../components/Toggle';
import { formatDateToISO } from '../utils/helpers';
import { useIsSmallScreen } from '../hooks/useIsSmallScreen';

export const StepTimeSlot = () => {
  const selectedSlot = useSelector((s: RootState) => s.appointment.selectedSlot);
  const patientType = useSelector((s: RootState) => s.appointment.patientType);
  const selectedTreatment = useSelector((s: RootState) => s.appointment.treatment);
  const selectedDurationMinutes = useSelector((s: RootState) => s.appointment.selectedDurationMinutes);
  const primetimeFilter = useSelector((s: RootState) => s.appointment.primetimeFilter);
  const { practice } = useSelector((s: RootState) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [currentStart, setCurrentStart] = useState<Date>(new Date());
  const [slotsByDate, setSlotsByDate] = useState<Record<string, Record<string, string[]>>>({});
  const proceed = () => {
    navigate(`/bookonline/step-4`);
  };

  const isSmallScreen = useIsSmallScreen();

  const daysToShow = isSmallScreen ? 3 : 7;

  const currentEnd = useMemo(() => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + (daysToShow - 1));
    return d;
  }, [currentStart, daysToShow]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res: AvailableSlotsResponse = await fetchAvailableSlots({
          start_date: formatDateToISO(currentStart),
          end_date: formatDateToISO(currentEnd),
          new_patient: patientType === 'new',
          fixed_duration_slots: true,
          appointment_type_id: selectedTreatment?.id,
          ...(practice?.promote_primetime && { filters: primetimeFilter }),
        });
        const map: Record<string, Record<string, string[]>> = {};
        const slotMap: Record<string, string[]> = {};
        for (const operatory of res || []) {
          const operatoryID = operatory?.id;
          const available = operatory?.attributes?.available_slots || {};
          for (const dateKey of Object.keys(available)) {
            const items = available[dateKey] || [];
            if (!map[dateKey]) map[dateKey] = {};
            for (const item of items) {
              // Use start_time as the slot label; keep mapping to pms operatory id
              const display = item.start_time;
              if (!map[dateKey][display]) {
                map[dateKey][display] = [];
              }
              map[dateKey][display].push(operatoryID);
              const slotId = `${dateKey}-${display}`;
              if (slotMap[slotId]) {
                slotMap[slotId].push(operatoryID);
              } else {
                slotMap[slotId] = [operatoryID];
              }
            }
          }
        }
        setSlotsByDate(map);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentStart, currentEnd, patientType, selectedTreatment?.id, selectedDurationMinutes, primetimeFilter]);

  const goPrevWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() - daysToShow);
    setCurrentStart(d);
  };
  const goNextWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + daysToShow);
    setCurrentStart(d);
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className='flex flex-col md:flex-row md:justify-between justify-center items-center'>
          <div className='flex flex-col items-center text-center md:items-start md:text-start'>
            <h1 className="text-2xl font-semibold text-black">When would you like to come?</h1>
            <p className="text-sm text-gray-500">Convenient evenings and weekends to fit your schedule</p>
          </div>
          {practice?.promote_primetime && (
            <div className='px-0 pt-5 md:pt-0 md:px-2'>
              <Toggle
                selected={primetimeFilter}
                onChange={(value) => dispatch(setPrimetimeFilter(value))}
              />
            </div>
          )}
        </div>
        <StepCard>
          {loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <SyncLoader />
            </div>
          ) : (
            <TimeSlotSelection
              selectedSlot={selectedSlot}
              onSlotSelect={(slot) => dispatch(setSelectedSlot(slot))}
              onOperatoriesSelect={(ops) => dispatch(setSelectedOperatories(ops))}
              slotsByDate={slotsByDate}
              onPrevWeek={goPrevWeek}
              onNextWeek={goNextWeek}
              currentStart={currentStart}
              daysToShow={daysToShow}
            />
          )}
        </StepCard>
        <div className="flex justify-between">
          <Button title='Back' variant='secondary' onClick={() => navigate('/bookonline/step-2')} />
          <Button title='Continue' disabled={!selectedSlot} onClick={proceed} />
        </div>
      </div>
    </Layout>
  );
};




